import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  GET_MESSAGE_LIST,
  GET_MESSAGE_THREAD,
} from 'shared/constants/storeTags'
import { AppDispatch, emptySplitApi, RootState } from 'shared/store/store'
import { DetailedMessage, MessageThread } from '../../../models/Message'
import { MessagesSectionEnum } from '../../../models/enum'
import messagesService from './messages.service'
import { getMessages, getMessagesSection } from './selectors'

const initialState = {
  loadingMore: false,
  section: MessagesSectionEnum.UnAnswered,
  isSendingMessage: false,
}

/**
 *
 * HELPERS
 */

export const removeThreadFromList = (payload: {
  threadId: string
  section: MessagesSectionEnum
}) =>
  messagesService.util.updateQueryData(
    'getMessages',
    payload.section,
    (draft) => {
      const index = draft.messageThreads.findIndex(
        ({ id }) => id === payload.threadId
      )
      if (index > -1) draft.messageThreads.splice(index, 1)
    }
  )

/**
 *
 * THUNKS
 */

export const handleNewThreadNotificationThunk = createAsyncThunk(
  'messages/handleNewThreadNotificationThunk',
  async (thread: MessageThread, { dispatch, getState }) => {
    const store = getState() as RootState
    // @ts-expect-error todo
    const currentSection = getMessagesSection(store)

    const newMessageSection =
      thread.createdBySystem === 'portal'
        ? MessagesSectionEnum.Answered
        : MessagesSectionEnum.UnAnswered

    // If the new message does not go into current section, we invalidate the cache
    if (newMessageSection !== currentSection) {
      dispatch(
        emptySplitApi.util.invalidateTags([
          { type: GET_MESSAGE_LIST, id: newMessageSection },
        ])
      )
      return
    }

    // Add the thread to list if not exists
    dispatch(
      messagesService.util.updateQueryData(
        'getMessages',
        currentSection,
        (draft) => {
          if (!draft.messageThreads.some(({ id }) => id === thread.id))
            draft.messageThreads.unshift(thread)
        }
      ) as AppDispatch
    )
  }
)

export const handleNewMessageNotificationThunk = createAsyncThunk(
  'messages/handleNewMessageNotificationThunk',
  async (message: DetailedMessage, { dispatch, getState }) => {
    const store = getState() as RootState
    // @ts-expect-error todo
    const section = getMessagesSection(store)
    const { data } = getMessages(section)(store)

    const isThreadOpen = window.location.pathname.includes(message.threadId)
    const isThreadInList = data?.messageThreads?.some(
      ({ id }) => id === message.threadId
    )
    const newMessageSection =
      message.sentBySystem === 'portal'
        ? MessagesSectionEnum.Answered
        : MessagesSectionEnum.UnAnswered

    // If the thread is open, push straight to cache, else invalidate cache
    if (isThreadOpen) {
      dispatch(
        messagesService.util.updateQueryData(
          'getMessageThread',
          message.threadId,
          (draft) => {
            if (!draft.some(({ messageId }) => messageId === message.messageId))
              draft.unshift(message)
          }
        ) as AppDispatch
      )
    } else {
      dispatch(
        emptySplitApi.util.invalidateTags([
          { type: GET_MESSAGE_THREAD, id: message.threadId },
        ])
      )
    }

    // If the new message goes in a thread that is not in the current list,
    // but is supposed to go there, we fetch it from API

    if (!isThreadInList && newMessageSection === section) {
      dispatch(
        messagesService.endpoints.getNewMessages.initiate(section, {
          forceRefetch: true,
        })
      )
      return
    }

    // Update list item
    dispatch(
      messagesService.util.updateQueryData('getMessages', section, (draft) => {
        const item = draft.messageThreads.find(
          (thread) => thread.id === message.threadId
        )
        if (item?.lastMessage) {
          item.lastMessage.text = message.text
          item.lastActive = message.updatedDate

          // Reset seenBy if the thread is not open
          if (!isThreadOpen) item.lastMessage.seenBy = []
        }
      }) as AppDispatch
    )
  }
)

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setSection: (state, action: PayloadAction<MessagesSectionEnum>) => {
      state.section = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      messagesService.endpoints.createThread.matchPending,
      (state) => {
        state.isSendingMessage = true
      }
    )
    builder.addMatcher(
      messagesService.endpoints.createThread.matchFulfilled,
      (state) => {
        state.isSendingMessage = false
      }
    )
    builder.addMatcher(
      messagesService.endpoints.createThread.matchRejected,
      (state) => {
        state.isSendingMessage = false
      }
    )
  },
})

export default messagesSlice
